import { displayDollar } from '../../lib/utils/helpers'
import { WithdrawalStatus } from '../../lib/types'
import {
  DashboardInvestmentDistribution,
  DashboardInvestmentTransactions,
  DashboardInvestmentsGraphs,
  Investment,
  InvestmentStatus_v1,
  RawOpportunity,
  UserDetails,
} from '../../lib/types/propsType_v1'
import { DISTRIBUTION_COLORS } from './dashboard-v2.constants'

export const buildPerformanceSeries = (graphs: DashboardInvestmentsGraphs) => {
  const byDate: Record<string, number> = {}
  graphs?.investments?.forEach((series) => {
    series?.forEach((point) => {
      byDate[point.date] = (byDate[point.date] || 0) + (point.currentInvestmentValue || 0)
    })
  })

  return Object.entries(byDate)
    .sort((a, b) => (a[0] > b[0] ? 1 : -1))
    .map(([date, value]) => ({ date, value }))
}

export const withUpperScaleSuffix = (value: string) =>
  value.replace(/([kmb])$/i, (m) => m.toUpperCase())

export const centsToCurrency = (n?: number) =>
  typeof n === 'number' ? withUpperScaleSuffix(displayDollar(String(n), 'USD')) : '$0'

export const signedClass = (n?: number) => {
  if (typeof n !== 'number' || Number.isNaN(n) || n === 0) return ''
  return n > 0 ? 'signed-positive' : 'signed-negative'
}

export const signedPercent = (n?: number) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return '0.0%'
  const abs = Math.abs(n).toFixed(2)
  if (n > 0) return `+${abs}%`
  if (n < 0) return `-${abs}%`
  return `${abs}%`
}

export const formatAxisDollar = (value: number) => {
  const abs = Math.abs(value)
  if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(2)}K`
  return `$${value.toFixed(2)}`
}

export const formatAxisDate = (isoDate: string) => {
  const d = new Date(isoDate)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
}

export const shouldShowWithdrawalProgressCard = (status?: string) =>
  status !== WithdrawalStatus.TRADE_EXECUTED &&
  status !== WithdrawalStatus.REJECTED &&
  status !== WithdrawalStatus.CANCELLED

const NON_CANCELLABLE_INVESTMENT_STATUSES: string[] = [
  InvestmentStatus_v1.CANCELLED,
  InvestmentStatus_v1.REJECTED,
  InvestmentStatus_v1.TRADE_EXECUTED,
  InvestmentStatus_v1.REFUND_PROCESSED,
]

const NON_CANCELLABLE_WITHDRAWAL_STATUSES: string[] = [
  WithdrawalStatus.CANCELLED,
  WithdrawalStatus.REJECTED,
  WithdrawalStatus.TRADE_EXECUTED,
]

/** Any in-progress investment on the dashboard v2 card (excludes terminal statuses). */
export const canCancelOngoingInvestment = (status?: string): boolean => {
  if (!status) return false
  return !NON_CANCELLABLE_INVESTMENT_STATUSES.includes(status)
}

/** Early-stage investments continue in the invest-v2 wizard. */
export const isOngoingInvestmentInitiated = (status?: string): boolean =>
  status === InvestmentStatus_v1.INITIATED

/**
 * ORDER_PLACED without SWIFT/wallet payment — investor can finish from the wallet
 * fund step without cancelling or re-signing the termsheet.
 */
export const investmentNeedsWalletPayment = (
  status?: string,
  metadata?: {
    paymentCopy?: string | null
    transactionNumber?: string | null
    walletDebited?: boolean | null
    payFromWallet?: boolean | null
  } | null,
): boolean => {
  if (status !== InvestmentStatus_v1.ORDER_PLACED) return false
  const meta = metadata || {}
  const paymentCopy = typeof meta.paymentCopy === 'string' ? meta.paymentCopy.trim() : ''
  const transactionNumber =
    typeof meta.transactionNumber === 'string' ? meta.transactionNumber.trim() : ''
  return !(paymentCopy || transactionNumber || meta.walletDebited || meta.payFromWallet)
}

/** Post-initiated investments show a read-only details modal instead of invest-v2. */
export const shouldOpenOngoingInvestmentDetailsModal = (status?: string): boolean => {
  if (!status) return false
  return !isOngoingInvestmentInitiated(status)
}

/** Withdrawal requests can be cancelled before payment settlement. */
export const canCancelOngoingWithdrawal = (status?: string): boolean => {
  if (!status) return false
  return !NON_CANCELLABLE_WITHDRAWAL_STATUSES.includes(status)
}

export const truncateLabel = (label: string, max = 24) => {
  if (!label) return ''
  return label.length > max ? `${label.slice(0, max)}...` : label
}

export const getLastUpdatedText = () => {
  const now = new Date()
  const date = now.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
  const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  return `Updated ${date}, ${time}`
}

export const buildDistributionItems = (investmentDistribution?: DashboardInvestmentDistribution) =>
  Object.entries(investmentDistribution || {}).map(([name, value], idx) => ({
    name,
    value: Number(value) || 0,
    color: DISTRIBUTION_COLORS[idx % DISTRIBUTION_COLORS.length],
  }))

export const buildStatusBreakdownData = (
  inProgressInvestments: Array<Investment & RawOpportunity & UserDetails>,
) => {
  const summary = { initiated: 0, processing: 0 }

  inProgressInvestments.forEach((inv) => {
    const amountInDollars = Number(inv.amount || 0) / 100
    if (inv.status === InvestmentStatus_v1.INITIATED) {
      summary.initiated += amountInDollars
    } else if (
      [InvestmentStatus_v1.PAYMENT_DONE, InvestmentStatus_v1.ORDER_PLACED].includes(inv.status)
    ) {
      summary.processing += amountInDollars
    }
  })

  return [
    { name: 'Initiated', value: summary.initiated, color: '#0D86FF' },
    { name: 'Processing', value: summary.processing, color: '#0B4E9D' },
  ]
}

// Formats transaction types like 'PAYMENT_RECEIVED' -> 'Payment Received'
// Falls back gracefully for unknown or already formatted values.
export const formatTransactionType = (raw?: string) => {
  if (!raw) return '-'
  const normalized = String(raw).trim().replace(/_/g, ' ').toLowerCase()
  return normalized.replace(/\b\w/g, (m) => m.toUpperCase())
}

export const isSellTransaction = (transactionType?: string): boolean =>
  transactionType?.trim().toUpperCase() === 'SELL'

export const normalizeTransactionAmountCents = (
  amount: number | string | null | undefined,
): number => {
  const n = Number(amount)
  if (!Number.isFinite(n)) return 0
  return Math.abs(Math.trunc(n))
}

export const normalizeTransactionQuantity = (
  quantity: number | string | null | undefined,
): number | null => {
  if (quantity === null || quantity === undefined || quantity === '') return null
  const n = Number(quantity)
  if (!Number.isFinite(n) || n === 0) return null
  return Math.abs(n)
}

/** Resolve API amount to cents; some sell rows send dollars while buys use cents. */
export const resolveTransactionAmountCents = (input: {
  amount?: number | string | null
  quantity?: number | string | null
  unit_price?: number | string | null
}): number => {
  const absAmount = normalizeTransactionAmountCents(input.amount)
  if (!absAmount) return 0

  const qty = normalizeTransactionQuantity(input.quantity)
  const unitPriceCents = normalizeTransactionAmountCents(input.unit_price)

  if (qty && unitPriceCents) {
    const derivedCents = Math.round(qty * unitPriceCents)
    if (derivedCents > 0) {
      const asCentsDiff = Math.abs(absAmount - derivedCents)
      const asDollarsCents = Math.round(absAmount * 100)
      const asDollarsDiff = Math.abs(asDollarsCents - derivedCents)

      if (asDollarsCents > 0 && asDollarsDiff < asCentsDiff) {
        return asDollarsCents
      }
      return absAmount
    }
  }

  return absAmount
}

export const formatTransactionAmountDisplay = (
  amountCents: number,
  transactionType?: string,
): string => {
  const absCents = normalizeTransactionAmountCents(amountCents)
  const formatted = displayDollar(String(absCents), 'USD', 2, true, true)
  return isSellTransaction(transactionType) ? `-${formatted}` : formatted
}

export const formatTransactionQuantityDisplay = (
  quantity: number | string | null | undefined,
): string => {
  const normalized = normalizeTransactionQuantity(quantity)
  if (normalized === null) return '—'
  const hasFraction = !Number.isInteger(normalized)
  return normalized.toLocaleString(undefined, {
    maximumFractionDigits: hasFraction ? 4 : 0,
    minimumFractionDigits: 0,
  })
}

/** Local calendar YYYY-MM-DD for matching graph points to transaction rows. */
export const calendarDateKey = (raw?: string): string => {
  if (!raw) return ''
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Unique formatted types per day (same strings as TransactionsTable type column). */
export const buildTransactionTypesByDate = (
  transactions: DashboardInvestmentTransactions[] | undefined,
): Map<string, string> => {
  const byDay = new Map<string, Set<string>>()
  if (!transactions?.length) return new Map()

  for (const t of transactions) {
    const rawType = t.transaction_type?.trim()
    if (!rawType || rawType === '-') continue
    const key = calendarDateKey(t.transaction_date)
    if (!key) continue
    const label = formatTransactionType(rawType)
    if (!byDay.has(key)) byDay.set(key, new Set())
    byDay.get(key)!.add(label)
  }

  const out = new Map<string, string>()
  byDay.forEach((set, key) => {
    out.set(key, [...set].sort().join(', '))
  })
  return out
}

export const PENDING_SHARES_ALLOTMENT_SUBTEXT =
  'Exact number of shares will be provided after MOA amendment.'

const hasExposureField = (value?: string | null): boolean => typeof value === 'string'

/** Non-fund holdings use exposure fields until (and after) shares are allotted. */
export const isUnderlyingExposureMode = (
  underlyingExposure?: string | null,
  underlyingExposureDescription?: string | null,
): boolean =>
  hasExposureField(underlyingExposure) || hasExposureField(underlyingExposureDescription)

export const formatUnitsOrSharesDisplay = (
  totalUnits: number | null | undefined,
  options?: { exposureMode?: boolean; pendingDash?: boolean },
): string => {
  const exposureMode = Boolean(options?.exposureMode)
  const units = typeof totalUnits === 'number' && !Number.isNaN(totalUnits) ? totalUnits : 0
  if (exposureMode && units <= 0) return '—'
  if (options?.pendingDash && units <= 0) return '—'
  if (!exposureMode) return units.toFixed(2)
  const hasFraction = !Number.isInteger(units)
  return units.toLocaleString(undefined, {
    maximumFractionDigits: hasFraction ? 2 : 0,
    minimumFractionDigits: 0,
  })
}
